<template>
  <section>
    <v-row>
      <v-col>
        <v-form ref="overrideForm" v-model="formValid" class="mt-0">
          <v-row class="justify-space-between align-start">
            <v-col class="col-4 pt-0">
              <v-card :elevation="0">
                <v-card-title>
                  <span v-if="absoluteSurchargeMode">
                    {{ $t('priceBulkManagement.absoluteSurcharge') }}
                  </span>
                  <span v-else>
                    {{ $t('priceBulkManagement.surcharge') }}
                  </span>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col class="col-12 px-0 py-1">
                      <v-text-field
                        v-if="absoluteSurchargeMode"
                        v-model="amount"
                        outlined
                        :rules="[
                          (v) => !!v || $t('rule.inputRequired'),
                          (v) => v >= 0 || 'Betrag muss grösser als 0 sein.',
                        ]"
                        :label="$t('priceBulkManagement.amount')"
                        :prefix="
                          $store.getters
                            .getCurrentDestinationInstance()
                            .getCurrency()
                        "
                        type="number"
                        step="0.01"
                      />
                      <v-text-field
                        v-else
                        v-model="amount"
                        outlined
                        :rules="[
                          (v) => (!!v && v < 301) || $t('rule.inputRequired'),
                        ]"
                        :label="$t('priceBulkManagement.percent')"
                        prefix="%"
                        type="number"
                        step="0.01"
                      />
                    </v-col>
                  </v-row>
                  <v-switch
                    v-model="absoluteSurchargeMode"
                    :label="
                      absoluteSurchargeMode
                        ? $t('priceBulkManagement.surchargeModeAbs')
                        : $t('priceBulkManagement.surchargeModePerc')
                    "
                    color="primary"
                    class="mt-0"
                  />
                </v-card-text>
              </v-card>
            </v-col>

            <v-col class="col-4 pt-0">
              <!-- Rules section -->
              <v-card v-if="!preselectedMode" :elevation="0">
                <v-card-title>
                  <span>
                    {{ $t('priceBulkManagement.editRules') }}
                  </span>
                </v-card-title>
                <v-card-text>
                  <rule-based-date-selector
                    :dense="false"
                    :date-range="dateRange"
                    @changed="updatedSelectedDates"
                  />
                </v-card-text>
              </v-card>
            </v-col>

            <!-- calendar preview -->
            <v-col class="col-4 pt-0">
              <v-card v-if="!preselectedMode" :elevation="0">
                <v-card-title class="py-0">
                  <v-row class="justify-space-between">
                    <v-col class="col-auto">
                      {{ $t('priceBulkManagement.preview') }}
                    </v-col>
                    <v-col class="col-auto text-right">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <span
                            class="fal fa-info-circle text--primary"
                            v-on="on"
                          />
                        </template>
                        <span>{{ $t('priceBulkManagement.explanation') }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text class="px-0">
                  <v-date-picker
                    id="preview-picker"
                    v-model="selectedDates"
                    full-width
                    :locale="$store.getters.getLanguageLocale()"
                    :min="minDatePriceOverride"
                    :max="maxDate"
                    first-day-of-week="1"
                    no-title
                    :show-current="false"
                    multiple
                    color="primary"
                    :allowed-dates="allowedDatesFunction"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="col-12 justify-space-between d-flex">
        <v-btn
          :disabled="!resetActivated"
          outlined
          color="secondary"
          large
          @click="showResetModal"
        >
          {{ $t('priceBulkManagement.reset') }}
        </v-btn>
        <v-btn
          :disabled="!overridePriceActivated"
          outlined
          color="primary"
          large
          @click="showConfirmModal"
        >
          {{ $t('priceBulkManagement.overwrite') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- confirm modal-->
    <price-override-confirm-pop-up
      :isReset="isReset"
      :shown="showOverwriteModal"
      :is-absolute="absoluteSurchargeMode"
      :amount="Math.round(parseFloat(amount) * 100) / 100"
      :overwrite-dates="sortedSelectedDates"
      :product-definitions="productDefinitions"
      :currentSelectedPrices="currentSelectedPrices"
      @ok="overridePrices()"
      @closed="showOverwriteModal = false"
    />
  </section>
</template>

<script>
import * as moment from 'moment'
import PriceOverwriteService from '@/classes-internal/priceOverwrite/PriceOverwriteService'
import RuleBasedDateSelector from '@/components-shared/products/RuleBasedDateSelector'
import Products from '@/classes-shared/products/Products'
import PriceOverrideConfirmPopUp from '@/components-internal/priceOverride/PriceOverrideConfirmPopUp'

export default {
  name: 'PriceOverrideForm',

  components: {
    PriceOverrideConfirmPopUp,
    RuleBasedDateSelector,
  },

  props: {
    chosenProducts: {
      required: true,
      type: Products,
    },
    dateRange: {
      required: true,
      type: Object,
    },
    preselectedMode: {
      required: false,
      type: Boolean,
      default: false,
    },
    //preselection of dates given on creation
    preSelectedDates: {
      required: false,
      type: Array,
      default: () => [],
    },
    selectedProductDefinitions: {
      required: false,
      type: Array,
      default: () => [],
    },
    currentSelectedPrices: {
      required: false,
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      absoluteSurchargeMode: true,
      formValid: false,
      amount: null,
      showOverwriteModal: false,
      overwriteService: new PriceOverwriteService(),
      selectedDates: [],
      isReset: false,
    }
  },

  computed: {
    maxDate() {
      if (this.dateRange.endDate) {
        return moment(this.dateRange.endDate).format('YYYY-MM-DD')
      }

      return null
    },
    productDefinitions() {
      return this.selectedProductDefinitions.map((prodDef) => prodDef.item)
    },
    minDate() {
      if (this.dateRange.startDate) {
        return moment(this.dateRange.startDate).format('YYYY-MM-DD')
      }
      return null
    },
    minDatePriceOverride() {
      if (!this.minDate || !this.dateRange.startDate) return null
      const today = moment()
      const dateRangeStart = moment(this.dateRange.startDate)
      return dateRangeStart.isBefore(today)
        ? today.format('YYYY-MM-DD')
        : dateRangeStart.format('YYYY-MM-DD')
    },
    sortedSelectedDates() {
      let tmp = this.selectedDates.slice()
      return tmp.sort()
    },
    resetActivated() {
      if (
        this.selectedProductDefinitions.length > 0 &&
        this.selectedDates.length > 0
      ) {
        return true
      }

      return false
    },
    overridePriceActivated() {
      if (
        this.selectedProductDefinitions.length > 0 &&
        this.selectedDates.length > 0 &&
        this.amount
      ) {
        return true
      }

      return false
    },
    availableDates() {
      if (this.chosenProducts && this.chosenProducts.products) {
        return this.chosenProducts.getAvailableDates('dateString')
      }
      return []
    },
  },

  watch: {
    // re-select products
    chosenProducts() {
      this.updatedSelectedDates()
    },
    // re-select dates
    dateRange: {
      handler() {
        this.updatedSelectedDates()
      },
      deep: true,
    },
  },

  mounted() {
    if (this.preSelectedDates.length > 0) {
      this.selectedDates = this.preSelectedDates
    }
  },

  methods: {
    allowedDatesFunction(val) {
      // allowing all dates if array is empty
      if (!this.availableDates.length) return true

      // restrict allowed dates
      return this.availableDates.indexOf(val) !== -1
    },
    showResetModal() {
      this.isReset = true
      this.showOverwriteModal = true
    },
    showConfirmModal() {
      this.isReset = false
      this.showOverwriteModal = true
    },
    updatedSelectedDates(dates = this.selectedDates) {
      // do not allow selected dates which are not in validity dates
      let newDates = []
      // consider validity dates
      if (this.availableDates.length) {
        newDates = dates.filter(
          (date) =>
            this.availableDates.find((validityDate) => validityDate === date) &&
            new Date(date).getTime() <= new Date(this.maxDate).getTime()
        )
      } else {
        newDates = dates.filter(
          (date) => new Date(date).getTime() <= new Date(this.maxDate).getTime()
        )
      }
      // inform user, if dates are restricted by validity dates
      if (newDates.length !== dates.length)
        EventBus.$emit(
          'notify',
          this.$t('pricingOverridePrices.notInValidityDates')
        )

      // Fix: problem with selected time zone
      const formatedStart = moment(this.dateRange.startDate).format(
        'YYYY-MM-DD'
      )
      const formatedEnd = moment(this.dateRange.endDate).format('YYYY-MM-DD')

      // filter by the selected date range
      newDates = newDates.filter((date) => {
        return (
          moment(date).isSameOrAfter(formatedStart) &&
          moment(date).isSameOrBefore(formatedEnd)
        )
      })
      // set new dates
      this.selectedDates = newDates
    },
    async overridePrices() {
      const prodDefIds = this.selectedProductDefinitions.map(
        (prodDef) => prodDef.item.id
      )
      if (this.isReset) {
        await this.overwriteService.resetPrices(this.selectedDates, prodDefIds)
      } else {
        let tmpAmount = parseFloat(this.amount)
        if (this.absoluteSurchargeMode) {
          tmpAmount = Math.round(tmpAmount * 100)
        }
        await this.overwriteService.overwritePrices(
          this.selectedDates,
          this.absoluteSurchargeMode,
          tmpAmount,
          prodDefIds
        )
      }
      this.$emit('postOverride')
    },
  },
}
</script>

<style scoped lang="scss">
#preview-picker {
  box-shadow: unset;
}
</style>
